import { MapPin, Anchor, Truck, CheckCircle2 } from "lucide-react";
import { Link } from "react-router-dom";

const FREE_ZONES = [
    "Walt Disney World Resorts",
    "Universal Orlando Resort Hotels",
    "SeaWorld Area",
    "International Drive",
    "Lake Buena Vista",
    "Kissimmee & 192 corridor",
    "Celebration & Champions Gate",
    "Most Orlando hotels within our service area",
];

const AREAS = [
    {
        city: "Orlando",
        icon: MapPin,
        image:
            "https://images.unsplash.com/photo-1556894546-d227c2d4df3b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzB8MHwxfHNlYXJjaHwxfHxvcmxhbmRvJTIwcGFsbSUyMHRyZWVzfGVufDB8fHx8MTc4NTgxNzUzN3ww&ixlib=rb-4.1.0&q=85",
        badge: "FREE",
        blurb:
            "Orange County Convention Center, Disney resorts, Universal hotels, SeaWorld area, International Drive, Lake Buena Vista, downtown Orlando, and most Orlando hotels within our service area.",
    },
    {
        city: "Kissimmee",
        icon: MapPin,
        image:
            "https://images.unsplash.com/photo-1781265285681-ac9aad43fc4b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzN8MHwxfHNlYXJjaHwzfHxtb2JpbGl0eSUyMHNjb290ZXIlMjBvdXRkb29yc3xlbnwwfHx8fDE3ODU4MTc1Mzd8MA&ixlib=rb-4.1.0&q=85",
        badge: "FREE",
        blurb:
            "Vacation homes on 192, Celebration, Reunion Resort, Champions Gate, and Walt Disney World area resorts — free delivery included.",
    },
    {
        city: "Port Canaveral",
        icon: Anchor,
        image:
            "https://images.unsplash.com/photo-1580541631950-7282082b53ce?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODd8MHwxfHNlYXJjaHwxfHxjcnVpc2UlMjBzaGlwJTIwZG9ja3xlbnwwfHx8fDE3ODU4MTc1Mzh8MA&ixlib=rb-4.1.0&q=85",
        badge: "$99 Concierge",
        blurb:
            "White-glove concierge delivery direct to your cruise terminal or nearby hotel — round-trip drop-off and pickup for Carnival, Disney, Royal Caribbean, MSC, and more. Minimum 4-day rental.",
    },
];

function Badge({ label }) {
    const free = label === "FREE";
    const concierge = label.toLowerCase().includes("concierge");
    return (
        <span
            data-testid={`area-fee-badge-${free ? "free" : concierge ? "concierge" : "paid"}`}
            className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-bold uppercase tracking-widest ${
                free
                    ? "bg-[color:var(--brand-green)] text-white"
                    : concierge
                      ? "bg-[color:var(--brand-gold)] text-[color:var(--brand-navy)]"
                      : "border border-slate-200 bg-white text-[color:var(--brand-navy)]"
            }`}
        >
            {label}
        </span>
    );
}

export default function DeliveryArea() {
    return (
        <div
            data-testid="delivery-area-page"
            className="mx-auto max-w-7xl px-4 pb-24 pt-16 sm:px-6 lg:px-8"
        >
            {/* FREE delivery banner */}
            <div
                data-testid="free-delivery-banner"
                className="mb-10 flex items-start gap-4 rounded-2xl border border-green-200 bg-green-50 p-6"
            >
                <span className="grid h-11 w-11 shrink-0 place-items-center rounded-full bg-[color:var(--brand-green)] text-white">
                    <Truck className="h-5 w-5" />
                </span>
                <div>
                    <div className="text-xs font-semibold uppercase tracking-widest text-[color:var(--brand-green-2)]">
                        Delivery Pricing
                    </div>
                    <h2 className="mt-1 font-display text-xl font-extrabold text-[color:var(--brand-navy)] sm:text-2xl">
                        FREE Delivery to Disney, Universal &amp; Most Orlando
                        Resorts
                    </h2>
                </div>
            </div>

            <div className="max-w-3xl">
                <div className="text-xs font-semibold uppercase tracking-widest text-[color:var(--brand-green)]">
                    Delivery Area
                </div>
                <h1 className="mt-2 font-display text-4xl font-extrabold tracking-tight text-[color:var(--brand-navy)] sm:text-5xl">
                    We come to you across Central Florida
                </h1>
                <p className="mt-5 text-slate-600">
                    Every rental includes hand-delivery to your hotel, vacation
                    home, or cruise terminal. Our team arrives on time with a
                    fully-charged scooter and a quick walkthrough.
                </p>
            </div>

            <div className="mt-12 grid gap-8 md:grid-cols-3">
                {AREAS.map((a) => (
                    <div
                        key={a.city}
                        data-testid={`area-card-${a.city.toLowerCase().replace(/\s/g, "-")}`}
                        className="card-surface overflow-hidden"
                    >
                        <img
                            src={a.image}
                            alt={a.city}
                            className="h-52 w-full object-cover"
                        />
                        <div className="p-6">
                            <div className="mb-3 flex items-center justify-between">
                                <a.icon className="h-5 w-5 text-[color:var(--brand-gold)]" />
                                <Badge label={a.badge} />
                            </div>
                            <h2 className="font-display text-2xl font-extrabold text-[color:var(--brand-navy)]">
                                {a.city}
                            </h2>
                            <p className="mt-2 text-sm leading-relaxed text-slate-600">
                                {a.blurb}
                            </p>
                        </div>
                    </div>
                ))}
            </div>

            {/* FREE zones list */}
            <div
                data-testid="free-zones-list"
                className="mt-16 card-surface p-8"
            >
                <div className="flex items-start gap-3">
                    <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-[color:var(--brand-green)] text-white">
                        <CheckCircle2 className="h-5 w-5" />
                    </span>
                    <div>
                        <div className="text-xs font-semibold uppercase tracking-widest text-[color:var(--brand-green-2)]">
                            Free delivery locations
                        </div>
                        <h3 className="mt-1 font-display text-xl font-extrabold text-[color:var(--brand-navy)]">
                            Included with every Orlando-area rental
                        </h3>
                    </div>
                </div>
                <ul className="mt-6 grid gap-3 sm:grid-cols-2">
                    {FREE_ZONES.map((z) => (
                        <li
                            key={z}
                            data-testid={`free-zone-item-${z.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                            className="flex items-center gap-3 text-sm text-slate-700"
                        >
                            <span className="inline-flex items-center rounded-full bg-[color:var(--brand-green)] px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest text-white">
                                Free
                            </span>
                            {z}
                        </li>
                    ))}
                </ul>
            </div>

            {/* Port Canaveral Concierge featured card */}
            <div
                data-testid="port-canaveral-concierge-featured"
                className="mt-8 overflow-hidden rounded-2xl border border-[color:var(--brand-gold)]/40 bg-[color:var(--brand-navy)] p-8 text-white sm:p-10"
            >
                <div className="grid gap-8 lg:grid-cols-5 lg:items-center">
                    <div className="lg:col-span-3">
                        <div className="inline-flex items-center gap-2 rounded-full bg-[color:var(--brand-gold)]/20 px-3 py-1 text-[10px] font-bold uppercase tracking-widest text-[color:var(--brand-gold)]">
                            <Anchor className="h-3.5 w-3.5" />
                            Concierge Service
                        </div>
                        <h3 className="mt-4 font-display text-3xl font-extrabold text-white sm:text-4xl">
                            Port Canaveral Concierge Delivery
                        </h3>
                        <p className="mt-3 max-w-lg text-slate-300">
                            A premium round-trip service for cruise passengers
                            — we hand-deliver your scooter, then meet you again
                            after your voyage. Zero logistics on your end.
                        </p>
                        <ul className="mt-6 grid gap-3 text-sm sm:grid-cols-2">
                            {[
                                "Minimum 4-Day Rental Required",
                                "Round-trip Delivery & Pickup",
                                "Direct to your cruise terminal or nearby hotel",
                                "Convenient pickup after your cruise",
                            ].map((f) => (
                                <li
                                    key={f}
                                    className="flex items-start gap-3 text-slate-200"
                                >
                                    <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-[color:var(--brand-gold)]" />
                                    <span>{f}</span>
                                </li>
                            ))}
                        </ul>
                    </div>
                    <div className="lg:col-span-2">
                        <div className="rounded-2xl bg-white/5 p-8 backdrop-blur">
                            <div className="text-[10px] font-bold uppercase tracking-widest text-[color:var(--brand-gold)]">
                                Flat Rate
                            </div>
                            <div className="mt-2 font-display text-6xl font-extrabold text-white">
                                $99
                            </div>
                            <div className="mt-1 text-sm text-slate-300">
                                All-inclusive round-trip
                            </div>
                            <Link
                                to="/scooters/pride-victory-10"
                                data-testid="concierge-book-link"
                                className="hero-cta pill-btn mt-6 inline-flex w-full items-center justify-center text-white"
                            >
                                Book Concierge Delivery
                            </Link>
                        </div>
                    </div>
                </div>
            </div>

            <div className="mt-16 grid gap-6 lg:grid-cols-3">
                <div className="card-surface p-8">
                    <Truck className="h-6 w-6 text-[color:var(--brand-green)]" />
                    <h3 className="mt-3 font-display text-xl font-extrabold text-[color:var(--brand-navy)]">
                        Free Orlando &amp; Kissimmee delivery
                    </h3>
                    <p className="mt-2 text-sm text-slate-600">
                        No delivery fee to any address inside our Orlando and
                        Kissimmee service areas.
                    </p>
                </div>
                <div className="card-surface p-8">
                    <MapPin className="h-6 w-6 text-[color:var(--brand-navy)]" />
                    <h3 className="mt-3 font-display text-xl font-extrabold text-[color:var(--brand-navy)]">
                        Outside our area?
                    </h3>
                    <p className="mt-2 text-sm text-slate-600">
                        We may still be able to help. Contact us with your
                        location and dates.
                    </p>
                </div>
                <div className="card-surface p-8">
                    <Anchor className="h-6 w-6 text-[color:var(--brand-gold)]" />
                    <h3 className="mt-3 font-display text-xl font-extrabold text-[color:var(--brand-navy)]">
                        Cruise passengers
                    </h3>
                    <p className="mt-2 text-sm text-slate-600">
                        Include your cruise line &amp; ship in the booking notes
                        — our concierge team coordinates porter drop-off and
                        post-cruise pickup at Port Canaveral.
                    </p>
                </div>
            </div>

            <div className="mt-16 flex flex-wrap items-center justify-between gap-6 rounded-3xl bg-[color:var(--brand-navy)] p-10 text-white">
                <div>
                    <h3 className="font-display text-2xl font-extrabold sm:text-3xl">
                        Ready to book?
                    </h3>
                    <p className="mt-2 text-slate-300">
                        Reserve the Pride Victory 10 with FREE delivery to
                        Disney, Universal, and most Orlando resorts.
                    </p>
                </div>
                <Link
                    to="/scooters/pride-victory-10"
                    data-testid="delivery-area-book-link"
                    className="hero-cta pill-btn text-white"
                >
                    Start Booking
                </Link>
            </div>
        </div>
    );
}
